import { compareTracksForTimeline } from '@strength/domain';
import type { TrackRow } from './timeline/types';

type SortableTrack = Pick<
  TrackRow,
  'id' | 'sortOrder' | 'parentTrackId' | 'createdAt'
>;

type GroupableTrack = Pick<TrackRow, 'id' | 'parentTrackId'>;

type PartitionableTrack = Pick<
  TrackRow,
  'id' | 'name' | 'parentTrackId' | 'archivedAt'
>;

/**
 * Whether `track` is an archived sub-track whose parent is not in the archived
 * list — i.e. one archived on its own, leaving its parent active. Its Archive
 * section group has to be reconstructed from a duplicated parent label rather
 * than from a real parent row.
 */
export function isOrphanedArchivedSubTrack<T extends GroupableTrack>(
  track: T,
  archivedTracks: readonly T[],
): boolean {
  return (
    track.parentTrackId != null &&
    !archivedTracks.some((t) => t.id === track.parentTrackId)
  );
}

/**
 * Nests each parent immediately in front of its children, preserving the
 * incoming order of both parents and children.
 *
 * Children whose parentTrackId matches no parent in the list are dropped by
 * default, the same as the server: `tracksForTeam` only emits parents and their
 * children. `retainOrphans` keeps them instead, grouped by parent and appended
 * after the real parent groups, so a sub-track archived independently of its
 * still-active parent survives the grouping.
 */
function groupParentsWithChildren<T extends GroupableTrack>(
  sorted: T[],
  { retainOrphans = false }: { retainOrphans?: boolean } = {},
): T[] {
  const childTracks = sorted.filter((t) => t.parentTrackId);
  const parents = sorted.filter((t) => !t.parentTrackId);
  const grouped = parents.flatMap((parent) => [
    parent,
    ...childTracks.filter((c) => c.parentTrackId === parent.id),
  ]);

  if (!retainOrphans) return grouped;

  const parentIds = new Set(parents.map((p) => p.id));
  const orphanParentIds: string[] = [];
  for (const child of childTracks) {
    const parentId = child.parentTrackId;
    if (parentId == null || parentIds.has(parentId)) continue;
    if (!orphanParentIds.includes(parentId)) orphanParentIds.push(parentId);
  }

  return [
    ...grouped,
    ...orphanParentIds.flatMap((parentId) =>
      childTracks.filter((c) => c.parentTrackId === parentId),
    ),
  ];
}

/**
 * Sorts tracks the way the server's `tracksForTeam` does, for the Zero data path.
 *
 * The ordering rule itself lives in `compareTracksForTimeline`
 * (`@strength/domain`) so the server, the Zero mutators' optimistic writes, and
 * this path can't drift apart
 */
export function sortTracks<T extends SortableTrack>(tracks: T[]): T[] {
  const sorted = [...tracks].sort((a, b) =>
    compareTracksForTimeline(
      { sortOrder: a.sortOrder ?? 0, createdAt: a.createdAt },
      { sortOrder: b.sortOrder ?? 0, createdAt: b.createdAt },
    ),
  );

  return groupParentsWithChildren(sorted);
}

/**
 * Sorts archived tracks alphabetically by name (A→Z), then nests each parent
 * in front of its children. A parent archived as a whole keeps its cascaded
 * children grouped beneath it; a sub-track archived on its own keeps its place
 * at the end of the list, where the Archive section can label its group from
 * the still-active parent.
 */
export function sortArchivedTracks<
  T extends Pick<TrackRow, 'id' | 'name' | 'parentTrackId'>,
>(tracks: T[]): T[] {
  const sorted = [...tracks].sort((a, b) => a.name.localeCompare(b.name));
  return groupParentsWithChildren(sorted, { retainOrphans: true });
}

/**
 * Splits tracks into active (archivedAt == null) and archived (archivedAt != null).
 * Active tracks keep their incoming order/logic untouched; archived tracks are
 * re-sorted alphabetically by name.
 */
export function partitionTracks<T extends PartitionableTrack>(
  tracks: T[],
): { active: T[]; archived: T[] } {
  const active = tracks.filter((t) => t.archivedAt == null);
  const archived = tracks.filter((t) => t.archivedAt != null);
  return { active, archived: sortArchivedTracks(archived) };
}
